<template>
    <div class="w-100 user-modal-register-payment-success-view px-2">
        <div class="mb-5 text-center">
            <p class="font-weight-bold font-size-200">{{ titleText }}</p>
            <p class="mb-0 font-size-125">{{ translate('You are awesome!') }}</p>
        </div>

        <div class="mb-5 d-flex justify-content-center">
            <div class="circle-loader" :class="{ 'load-complete': finishLoader }">
                <div class="checkmark draw" :class="{ 'd-block': finishLoader }"></div>
            </div>
        </div>

        <div class="mb-5 text-center">
            <p class="mb-0 font-size-125" v-if="isTrial">{{ translate('Your free trial started.') }}</p>
            <p class="mb-0 font-size-125" v-if="isTrial && trialMainText">{{ trialMainText }}</p>
            <p class="mb-5 font-size-125">{{ translate('If you have any questions feel free to get in touch.') }}</p>
        </div>

        <ButtonWithLoadingState
            data-user-modal-button-submit
            class="btn btn-success btn-lg btn-block font-weight-bold text-uppercase rounded mb-4"
            icon="fa fa-arrow-right lh-1"
            :text="translate('Continue')"
            :is-state-loading="isSubmitting"
            :show-icon-after-text="true"
            @click.stop.prevent="doContinue"
        ></ButtonWithLoadingState>
    </div>
</template>

<script>
import ButtonWithLoadingState from '../../../../../qaamgo/modules/sat/components/form/ButtonWithLoadingState';
import TransFilter from '../../../../../qaamgo/modules/sat/components/mixins/TransFilter';
import { pageStoreMixin } from '../../../../store/page-store';
import { translate } from '../../../../../qaamgo/helper/translate';
import { projectHelper } from '../../../../misc/project-helper';
import { userModalRoute } from '../router';
import { getLocationHashWithoutModalPageFragment } from '../modal-fragment-helper';
import { launchConfetti, launchConfettiFireworks, launchLesserConfetti } from '../../../../misc/confetti';

export default {
    name: 'RegisterPaymentSuccessView',
    components: { ButtonWithLoadingState },
    mixins: [TransFilter, pageStoreMixin],
    props: {
        isTrial: {
            type: Boolean,
            default: false,
        },
        countDaysReminderBeforeTrialEnd: {
            type: Number,
            default: 0,
        },
        boughtSubscription: {
            type: Boolean,
            default: false,
        },
    },
    beforeMount() {
        this.checkIfMobile();
        window.addEventListener('resize', this.checkIfMobile);
    },
    beforeUnmount() {
        window.removeEventListener('resize', this.checkIfMobile);
    },
    mounted() {
        setTimeout(() => {
            this.store.userPaymentModal.setSubmitting(false);

            this.finishLoader = true;

            if (this.isMobile) {
                launchLesserConfetti();

                return;
            }

            launchConfetti('register-payment-flow-modal-content');
            launchConfettiFireworks();
        }, 500);
    },
    computed: {
        /** @return {Boolean} */
        isSubmitting() {
            return this.store.userPaymentModal.isSubmitting;
        },
        /** @return {User} */
        storeUser() {
            return this.store.user.getUser;
        },
        titleText() {
            if (projectHelper.isApi2Convert() || !this.boughtSubscription) {
                return translate('Thank you for your purchase on %projectName%.', {
                    '%projectName%': projectHelper.getProjectName(),
                });
            }

            return translate('Thank you for subscribing');
        },
        trialMainText() {
            if (this.isTrial && this.countDaysReminderBeforeTrialEnd > 0) {
                return translate('We will notify you %count% days before your trial ends.', {
                    '%count%': this.countDaysReminderBeforeTrialEnd,
                });
            }

            return '';
        },
    },
    methods: {
        checkIfMobile() {
            this.isMobile = window.matchMedia('(max-width: 600px)').matches;
        },
        doContinue() {
            // TODO maybe set flag if a new account was created? i.e. don't show mail text for old unconfirmed accounts
            if (this.storeUser.isEnabled()) {
                this.store.userPaymentModal.close();
                // reload window to make sure user/js has correct state
                window.location.hash = getLocationHashWithoutModalPageFragment();

                window.allowLeave = true;
                window.location.reload();

                return;
            }

            this.$router.push({
                name: userModalRoute.REGISTER_CONFIRMATION_MAIL_SENT,
            });
        },
    },
    data() {
        return {
            finishLoader: false,
            isMobile: false,
        };
    },
};
</script>

<style lang="scss">
$brand-success: #00c851;
$loader-size: 7em;
$check-height: calc($loader-size / 2);
$check-width: calc($check-height / 2);
$check-left: (calc($loader-size / 6) + calc($loader-size / 12));
$check-thickness: 4px;
$check-color: $brand-success;

.circle-loader {
    border: 3px solid rgba(0, 0, 0, 0.2);
    border-left-color: $check-color;
    animation: loader-spin 1.2s infinite linear;
    position: relative;
    display: inline-block;
    vertical-align: top;
    border-radius: 50%;
    width: $loader-size;
    height: $loader-size;

    @media (prefers-reduced-motion) {
        animation: none;
    }
}

.load-complete {
    animation: none;
    border-color: $check-color;
    transition: border 500ms ease-out;
}

.checkmark {
    display: none;

    &.draw:after {
        animation-duration: 800ms;
        animation-timing-function: ease;
        animation-name: checkmark;
        transform: scaleX(-1) rotate(135deg);

        @media (prefers-reduced-motion) {
            animation: none;
        }
    }

    &:after {
        content: '';
        opacity: 1;
        height: $check-height;
        width: $check-width;
        transform-origin: left top;
        border-right: $check-thickness solid $check-color;
        border-top: $check-thickness solid $check-color;
        left: 22px;
        top: 49px;
        position: absolute;
    }
}

@keyframes loader-spin {
    0% {
        transform: rotate(0deg);
    }
    100% {
        transform: rotate(360deg);
    }
}

@keyframes checkmark {
    0% {
        height: 0;
        width: 0;
        opacity: 1;
    }
    20% {
        height: 0;
        width: $check-width;
        opacity: 1;
    }
    40% {
        height: $check-height;
        width: $check-width;
        opacity: 1;
    }
    100% {
        height: $check-height;
        width: $check-width;
        opacity: 1;
    }
}
</style>
